import { useAsync } from 'react-use';

import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { WatchtowerPullRequestApiRef } from '../api/WatchtowerPullRequestApi';
import { PullRequestType } from '../types';

const PR_TYPES: PullRequestType[] = [
  'needsReview',
  'returned',
  'approved',
  'opened',
  'waitingAuthor',
  'merged',
];

export type PullRequestCounts = {
  [key in PullRequestType]: number | undefined;
};

export const useAllPullRequestCounts = (refreshKey = 0) => {
  const watchtowerApi = useApi(WatchtowerPullRequestApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const asyncState = useAsync(async () => {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    const userEntity = await catalogApi.getEntityByRef(userEntityRef);
    const userGithubId =
      userEntity?.metadata?.annotations?.['github.com/user-database-id'];

    if (!userGithubId) {
      return PR_TYPES.reduce(
        (acc, type) => ({ ...acc, [type]: 0 }),
        {} as PullRequestCounts,
      );
    }

    const apiCalls = PR_TYPES.map(type => {
      const apiMethod = {
        approved: 'getPullRequestsApprovedCount',
        merged: 'getPullRequestsRecentlyMergedCount',
        needsReview: 'getPullRequestsNeedsReviewCount',
        opened: 'getPullRequestsOpenedCount',
        returned: 'getPullRequestsReturnedToUserCount',
        waitingAuthor: 'getPullRequestsWaitingForAuthorCount',
      }[type] as
        | 'getPullRequestsApprovedCount'
        | 'getPullRequestsRecentlyMergedCount'
        | 'getPullRequestsNeedsReviewCount'
        | 'getPullRequestsOpenedCount'
        | 'getPullRequestsReturnedToUserCount'
        | 'getPullRequestsWaitingForAuthorCount';

      return watchtowerApi[apiMethod](userGithubId);
    });

    const results = await Promise.allSettled(apiCalls);

    const counts = PR_TYPES.reduce((acc, type, index) => {
      const result = results[index];
      let count: number | undefined;

      if (result.status === 'fulfilled') {
        count = result.value.count;
      } else {
        count = undefined;
      }

      return { ...acc, [type]: count };
    }, {} as PullRequestCounts);

    return counts;
  }, [watchtowerApi, identityApi, catalogApi, refreshKey]);

  return {
    counts: asyncState.value,
    loading: asyncState.loading,
    error: asyncState.error,
  };
};
